/**
 * ToastContext.js
 *
 * Global toast / notification system using React Context + framer-motion.
 * Replaces per-component toast state scattered across Dashboard, Support, etc.
 *
 * Usage:
 *   // Wrap your app:
 *   import { ToastProvider } from './utils/ToastContext';
 *   <ToastProvider><App /></ToastProvider>
 *
 *   // In any component:
 *   import { useToast } from './utils/ToastContext';
 *   const { showToast } = useToast();
 *   showToast('Payment submitted!', 'success');
 *   showToast('Network error', 'error', 6000);    // custom duration
 */

import React, { createContext, useCallback, useContext, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

// ======================== CONTEXT ========================
const ToastContext = createContext(null);

// ======================== ICONS ========================
const ToastIcons = {
    success: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#22C55E" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M22 11.08V12a10 10 0 11-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
    ),
    error: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#EF4444" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
    ),
    info: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#3B82F6" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    ),
    warning: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#F59E0B" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
    ),
};

const TOAST_STYLES = {
    success: { border: '1px solid rgba(34,197,94,0.4)', background: 'rgba(34,197,94,0.1)', accent: '#22C55E' },
    error: { border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.1)', accent: '#EF4444' },
    info: { border: '1px solid rgba(59,130,246,0.4)', background: 'rgba(59,130,246,0.1)', accent: '#3B82F6' },
    warning: { border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.1)', accent: '#F59E0B' },
};

// ======================== SINGLE TOAST ========================
function Toast({ id, message, type, onDismiss }) {
    const style = TOAST_STYLES[type] || TOAST_STYLES.info;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, x: 80, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 80, scale: 0.9 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 16px',
                borderRadius: 14,
                background: 'rgba(15,23,42,0.92)',
                border: style.border,
                backdropFilter: 'blur(16px)',
                boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px ${style.accent}20`,
                minWidth: 260, maxWidth: 360,
                fontFamily: "'Inter', sans-serif",
                fontSize: 14, color: '#F8FAFC',
            }}
        >
            <div style={{ flexShrink: 0 }}>{ToastIcons[type]}</div>
            <span style={{ flex: 1, lineHeight: 1.5 }}>{message}</span>
            <button
                onClick={() => onDismiss(id)}
                style={{
                    background: 'none', border: 'none', color: '#64748B',
                    cursor: 'pointer', fontSize: 18, padding: '0 2px',
                    lineHeight: 1, flexShrink: 0,
                }}
            >×</button>
            {/* Auto-progress bar */}
            <style>{`
                @keyframes toast-shrink { from { width: 100%; } to { width: 0%; } }
            `}</style>
        </motion.div>
    );
}

// ======================== PROVIDER ========================
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const dismiss = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    /**
     * @param {string} message
     * @param {'success'|'error'|'info'|'warning'} type
     * @param {number} duration Ms before auto-dismiss. 0 = persistent.
     */
    const showToast = useCallback((message, type = 'info', duration = 4000) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        setToasts(prev => [...prev, { id, message, type }]);
        if (duration > 0) {
            setTimeout(() => dismiss(id), duration);
        }
        return id;
    }, [dismiss]);

    return (
        <ToastContext.Provider value={{ showToast, dismiss }}>
            {children}

            {/* Toast Stack — fixed top-right */}
            <div style={{
                position: 'fixed', top: 20, right: 20, zIndex: 99999,
                display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10,
                pointerEvents: 'none',
            }}>
                <AnimatePresence mode="sync">
                    {toasts.map(toast => (
                        <div key={toast.id} style={{ pointerEvents: 'auto' }}>
                            <Toast
                                id={toast.id}
                                message={toast.message}
                                type={toast.type}
                                onDismiss={dismiss}
                            />
                        </div>
                    ))}
                </AnimatePresence>
            </div>
        </ToastContext.Provider>
    );
}

// ======================== HOOK ========================
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within a <ToastProvider>');
    return ctx;
}
